import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { supabase } from '../../utilities/supabase';
import { UserProfileContext } from '../../App';
import { EntityTypeEnum } from '../../utilities/Enumerables';
import { PERMISSIONS } from '../../utilities/Permissions';

// PORTAL_VIEW_V1: the top bar switch (Assisted · Portal · Guide · All) is each
// staff member's own view filter. Every EntityTable list whose model has
// `static audienceFilter = true` only shows that audience (the `audience`
// column / computed field in src/database/portal-funnel-switch/portal_view.sql).
// Saved on the account (user_profiles.second_band_route) via
// crm_set_second_band, read back with crm_second_band; 'all' = no filter.
// AUDIENCE_SWITCH_V1: only roles with Settings 'View Audience Switch' get the switch. Anyone
// without it sees everyone ('all'), never a filter saved earlier that they can no longer change.

const PortalViewContext = createContext({ view: 'assisted', setView: () => {}, saving: false, error: null });

export const PortalViewProvider = ({ children }) => {
  const { userProfile } = useContext(UserProfileContext);
  const canSwitch = !!userProfile?.hasPermission?.(
    EntityTypeEnum.Settings,
    PERMISSIONS[EntityTypeEnum.Settings].VIEW_AUDIENCE_SWITCH
  );
  const [view, setViewState] = useState('assisted'); // everyone starts on Assisted (owner, 13 Oct)
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    supabase.rpc('crm_second_band').then(({ data, error }) => {
      if (!error && data?.route) setViewState(data.route);
    });
  }, []);

  const setView = useCallback(async next => {
    let previous;
    setViewState(current => {
      previous = current;
      return next;
    });
    setSaving(true);
    setError(null);
    const { data, error } = await supabase.rpc('crm_set_second_band', { p_route: next });
    setSaving(false);
    if (error) {
      setError(error.message);
      setViewState(previous);
      return;
    }
    setViewState(data?.route || 'assisted');
  }, []);

  return (
    <PortalViewContext.Provider value={{ view: userProfile && !canSwitch ? 'all' : view, setView, saving, error }}>
      {children}
    </PortalViewContext.Provider>
  );
};

export const usePortalView = () => useContext(PortalViewContext);

// The filter EntityTable adds for a model, or null. Never inside one client's
// or lead's own record (its Traces / Searches / Pensions tabs), where every
// row belongs to that one person anyway.
export const audienceApplies = (model, parentContext = null) =>
  Boolean(model?.audienceFilter) && !/^entity_(20|56)_/.test(parentContext?.type || '');

export const audienceFilterFor = (model, view, parentContext = null) =>
  audienceApplies(model, parentContext) && view && view !== 'all' ? { operator: 'eq', value: view } : null;
